/**
 * @fileoverview Memory Cleanup Hook
 * 
 * Handles automatic cleanup of old jobs to prevent memory leaks.
 * Runs periodically to remove stale completed/failed jobs.
 */

import { useEffect, useCallback } from 'react';
import { RECENT_COMPLETED_WINDOW_MS, triggerGarbageCollection } from '../../utils/optimizationJobUtils';
import type { OptimizationJob } from './types';

export interface UseJobCleanupOptions {
  /** Jobs to clean up */
  jobs: OptimizationJob[];
  /** Cleanup interval in milliseconds (default: 60000 - 1 minute) */
  cleanupInterval?: number;
  /** Callback when jobs should be cleaned */
  onCleanup: (jobsToKeep: OptimizationJob[]) => void;
}

/**
 * Filter jobs that should be kept in memory
 */
function filterJobsForRetention(jobs: OptimizationJob[]): OptimizationJob[] {
  const now = Date.now();

  return jobs.filter((job) => {
    // Always keep running and queued jobs
    if (job.status === 'running' || job.status === 'queued') {
      return true;
    }

    // For completed/failed jobs, only keep them within the retention window
    const jobTime = job.completedAt
      ? new Date(job.completedAt).getTime()
      : new Date(job.startTime).getTime();
    const age = now - jobTime;

    return age < RECENT_COMPLETED_WINDOW_MS;
  });
}

/**
 * Hook for automatic job cleanup to prevent memory leaks
 */
export function useJobCleanup(options: UseJobCleanupOptions): void {
  const { jobs, cleanupInterval = 60000, onCleanup } = options;

  const performCleanup = useCallback(() => {
    const jobsToKeep = filterJobsForRetention(jobs);

    if (jobsToKeep.length !== jobs.length) {
      onCleanup(jobsToKeep);
      triggerGarbageCollection();
    }
  }, [jobs, onCleanup]);

  useEffect(() => {
    const interval = setInterval(performCleanup, cleanupInterval);
    return () => clearInterval(interval);
  }, [performCleanup, cleanupInterval]);
}
